import { h, Component } from 'preact';

import './index.less';

import I18n from '../../lib/I18n';
import EventBus from '../../lib/EventBus';
import { getIdentity, INTENT, setIdentity } from '../../lib/Identity';
import { createOrGetVisitor, getDownloadLink } from '../../lib/Visitor';
import { getDomainTag } from '../../lib/Amalgam';
import { getCountryFromCallingCode, getCountryFromCountryCode } from '../../lib/Country';
import { sendSMS, validateCode, shouldVerifyOTP } from '../../lib/Otp';
import { getActionButtonTheme, getCatalogueTheme } from '../../lib/Theme';
import { cdnHost } from '../../../config';
import { parsePhone, stripNumberOfPlusIfExists } from '../../lib/Utils';
import { onRequestIdentityModalCompleted, onRequestIdentityModalShown } from '../../lib/Events';
import CountryCallingCodeSelect from '../../molecules/CountryCallingCodeSelect';
import { validatePhoneNumber } from '../../lib/PhoneUtils';
import ArrowLongRight from '../../molecules/Icon/ArrowLongRight';
import RoundedRight from '../../molecules/Icon/RoundedRight';
import Multiply from '../../molecules/Icon/Multiply';
import Modal from '../../molecules/Modal';
import OTPError from '../../organisms/OTPError';

const RENDER_ACTION_BUTTON = 'RENDER_ACTION_BUTTON';
const RENDER_PHONE_INPUT = 'RENDER_PHONE_INPUT';
const RENDER_OTP_INPUT = 'RENDER_OTP_INPUT';

class RequestIdentityModal extends Component {
  constructor (props) {
    super(props);

    this.INITIAL_STATE = {
      intent: null,
      otpFlowStarted: false,
      showResendOtp: false,
      isLoading: false,
      showResellButton: false,
      selectedCountry: getCountryFromCountryCode('IN'),
      nationalNumber: '',
      otpCode: '',
      nationalNumberInvalid: false,
      otpCodeInvalid: false,
      resellDownloadLink: '',
      error: null
    };

    this.state = this.INITIAL_STATE;
  }

  onDone = () => {};
  onCancel = () => {};

  componentDidMount () {
    EventBus.addListener(EventBus.TYPE.SHOW_REQUEST_IDENTITY_MODAL, this.show);
    EventBus.addListener(EventBus.TYPE.HIDE_REQUEST_IDENTITY_MODAL, this.hide);

    createOrGetVisitor()
      .then((visitor) => (visitor ? visitor.getCountryCode() : null))
      .then((countryCode) => {
        if (countryCode) {
          this.setState({
            selectedCountry: getCountryFromCountryCode(countryCode)
          });
        }
      });
  }

  componentWillUnmount () {
    EventBus.removeListener(EventBus.TYPE.SHOW_REQUEST_IDENTITY_MODAL, this.show);
    EventBus.removeListener(EventBus.TYPE.HIDE_REQUEST_IDENTITY_MODAL, this.hide);
  }

  show = ({ intent, onDone, onCancel }) => {
    const { otpVerificationRequired = false } = this.props;
    this.onDone = onDone || (() => {});
    this.onCancel = onCancel || (() => {});

    getIdentity()
      .then((identity) => {
        const stateUpdates = { intent };

        if (intent === INTENT.RESELL) {
          stateUpdates.resellDownloadLink = getDownloadLink();
        }

        const domainTag = getDomainTag();

        if (domainTag) {
          if (domainTag.qs_phone_country) {
            stateUpdates.selectedCountry = getCountryFromCallingCode(
              '+' + domainTag.qs_phone_country
            );
          }

          if (domainTag.qs_phone_number) {
            stateUpdates.nationalNumber = domainTag.qs_phone_number;
          }
        }

        if (!identity.phone || intent === INTENT.CHANGE_PHONE) {
          if (intent === INTENT.CHANGE_PHONE && identity.phone) {
            const { callingCode, number } = parsePhone(identity.phone);

            stateUpdates.selectedCountry = getCountryFromCallingCode(callingCode || '+91');
            stateUpdates.nationalNumber = number;
          }

          onRequestIdentityModalShown();
          this.setState(stateUpdates);
          return;
        }

        stateUpdates.nationalNumber = identity.phone;

        if ((shouldVerifyOTP() && !identity.phoneVerified) || otpVerificationRequired) {
          this.startOtpFlow(identity.phone);
          onRequestIdentityModalShown();
          this.setState(stateUpdates);
          return;
        }

        this.onDone();
      })
      .catch(() => {});
  };

  hide = () => {
    this.onClose();
  };

  startOtpFlow = (phoneNumber) => {
    if (this.state.otpFlowStarted) {
      return;
    }

    this.setState({
      otpFlowStarted: true,
      isLoading: true,
      nationalNumber: phoneNumber,
      error: null
    });

    sendSMS(phoneNumber)
      .then(() => {
        this.setState(
          {
            isLoading: false,
            otpSent: true,
            showResendOtp: true
          },
          () => {
            if (this.otpInput) {
              this.otpInput.focus();
            }
          }
        );
      })
      .catch((error) => {
        this.setState({ isLoading: false, error });
      });
  };

  onClose = () => {
    this.setState({
      ...this.INITIAL_STATE,
      selectedCountry: this.state.selectedCountry,
      error: null
    });
    this.onCancel();
  };

  onNumberChange = (e) => {
    this.setState({
      nationalNumber: e.target.value,
      nationalNumberInvalid: false,
      error: null
    });
  };

  onOtpChange = (e) => {
    this.setState({
      otpCode: e.target.value,
      otpCodeInvalid: false
    });
  };

  onCountryChange = (e) => {
    this.setState({
      selectedCountry: JSON.parse(e.target.value),
      error: null
    });
  };

  onResellClick = () => {
    this.onClose();
  };

  onChangePhone = () => {
    this.setState({
      otpFlowStarted: false,
      otpSent: false,
      nationalNumber: null,
      error: null
    });
  };

  onResendCode = () => {
    this.setState(
      {
        otpCode: '',
        otpFlowStarted: false,
        showResendOtp: false
      },
      () => {
        this.startOtpFlow(this.state.nationalNumber);
      }
    );
  };

  submit = (event) => {
    event.preventDefault();
    const { intent, selectedCountry, nationalNumber: nationalNumberRaw } = this.state;
    const { otpVerificationRequired } = this.props;

    if (!nationalNumberRaw) {
      this.setState({ nationalNumberInvalid: true });
      return;
    }

    this.setState({ isLoading: true });

    const callingCode = selectedCountry.callingCode.split(' ').join('');
    validatePhoneNumber({ callingCode, nationalNumberRaw })
      .then((result) => {
        const { e164Number: proper164Number } = result;

        setIdentity({
          phone: proper164Number,
          phoneVerified: false
        }).then((identity) => {
          if (!identity) {
            return;
          }

          if (shouldVerifyOTP() || otpVerificationRequired) {
            this.startOtpFlow(identity.phone);
          } else if (intent === INTENT.RESELL) {
            this.setState({ showResellButton: true });
          } else {
            this.onDone();
            this.setState(this.INITIAL_STATE);
            onRequestIdentityModalCompleted();
          }
        });
      })
      .catch(() => {
        this.setState({ nationalNumberInvalid: true, isLoading: false });
      });
  };

  verifyOTP = ({ onOtpCodeValidate } = {}) => {
    const otpCode = this.state.otpCode ? this.state.otpCode.toString() : null;

    if (!otpCode || otpCode.length !== 4) {
      this.setState({ otpCodeInvalid: true });
      return;
    }

    this.setState({ isLoading: true });

    validateCode(this.state.nationalNumber, otpCode)
      .then((done) => {
        if (!done) {
          this.setState({ otpCodeInvalid: true });
          return Promise.reject();
        }

        if (typeof onOtpCodeValidate === 'function') {
          return onOtpCodeValidate({ phone: stripNumberOfPlusIfExists(this.state.nationalNumber) });
        }
      })
      .then(() => {
        setIdentity({
          phone: this.state.nationalNumber,
          phoneVerified: true
        }).then(() => {
          this.onDone();
          this.setState(this.INITIAL_STATE);
          onRequestIdentityModalCompleted();
        });
      })
      .catch(() => {
        this.setState({ isLoading: false });
      });
  };

  getButtonText = ({ text } = {}) => {
    const { intent, otpFlowStarted } = this.state;
    const { otpVerificationRequired } = this.props;

    if (typeof text === 'string') {
      return text;
    }

    if ((shouldVerifyOTP() || otpVerificationRequired) && !otpFlowStarted) {
      return I18n.t('REQUEST_IDENTITY_MODAL_VERIFY_PHONE_NUMBER');
    }

    if (intent === INTENT.RESELL) {
      return I18n.t('REQUEST_IDENTITY_MODAL_RESELL_CATALOGUE');
    }

    if (intent === INTENT.CHANGE_PHONE) {
      return I18n.t('CHANGE_PHONE');
    }

    return getActionButtonTheme().text;
  };

  renderPhoneInput = ({
    phoneInputTitle,
    inputContainerClass = '',
    outerFieldClass = '',
    inputClass = '',
    theme: {
      titleStyle = {},
      styleForLeftContent = {},
      styleForRightContent = {},
      outerStyle = {},
      outerInputStyle = {}
    } = {}
  } = {}) => {
    const {
      otpFlowStarted,
      showResendOtp,
      isLoading,
      selectedCountry,
      nationalNumber,
      nationalNumberInvalid
    } = this.state;

    return (
      <div
        className={`input phone ${inputContainerClass} ${nationalNumberInvalid ? 'invalid' : ''}`}
      >
        <div style={titleStyle} className="title">
          {phoneInputTitle || (
            <span style={styleForLeftContent} className="left">
              {I18n.t('WHATSAPP_NUMBER')}
            </span>
          )}
          {otpFlowStarted && showResendOtp && (
            <span style={styleForRightContent} className="right" onClick={this.onChangePhone}>
              {I18n.t('CHANGE_NUMBER')}
            </span>
          )}
        </div>

        <div className="field">
          <div style={outerStyle} className={`outer ${outerFieldClass}`}>
            {!otpFlowStarted && (
              <CountryCallingCodeSelect
                containerClassName="country"
                selectedCountry={selectedCountry}
                onCountryChange={this.onCountryChange}
                style={outerInputStyle}
              />
            )}
            <div className={'input-container'}>
              <input
                type="tel"
                className={inputClass}
                placeholder={I18n.t('PHONE')}
                readOnly={otpFlowStarted || isLoading}
                value={nationalNumber}
                onInput={this.onNumberChange}
                style={outerInputStyle}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  renderOtpInput = ({
    otpInputTitle,
    hideResend = false,
    verticalFieldEndData = null,
    inputContainerClass = '',
    outerFieldClass = '',
    inputClass = '',
    theme: {
      titleStyle = {},
      styleForLeftContent = {},
      styleForRightContent = {},
      outerStyle = {},
      outerInputStyle = {}
    } = {}
  } = {}) => {
    const { otpFlowStarted, showResendOtp, isLoading, otpCode, otpCodeInvalid } = this.state;

    if (!otpFlowStarted) {
      return null;
    }

    return (
      <div className={`input otp ${inputContainerClass} ${otpCodeInvalid ? 'invalid' : ''}`}>
        <div style={titleStyle} className="title">
          {otpInputTitle || (
            <span style={styleForLeftContent} className="left">
              {I18n.t('OTP_CODE')}
            </span>
          )}

          {!hideResend && (
            <div className="right">
              {showResendOtp && (
                <span style={styleForRightContent} onClick={this.onResendCode}>
                  {I18n.t('RESEND_CODE')}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="field">
          <div style={outerStyle} className={`outer ${outerFieldClass}`}>
            <div className="input-container">
              <input
                ref={(element) => (this.otpInput = element)}
                type="number"
                placeholder="1234"
                disabled={isLoading}
                value={otpCode}
                onInput={this.onOtpChange}
                className={inputClass}
                style={outerInputStyle}
              />
            </div>
          </div>
        </div>
        {verticalFieldEndData}
      </div>
    );
  };

  handleActionButtonClick = ({ onOtpCodeValidate } = {}, event) => {
    const { otpFlowStarted } = this.state;
    if (otpFlowStarted) {
      this.verifyOTP({ onOtpCodeValidate });
      return;
    }

    this.submit(event);
  };

  renderActionButton = ({
    text,
    buttonContainerClass = '',
    buttonClass = '',
    loaderClass = '',
    iconClass = '',
    onOtpCodeValidate,
    theme: {
      actionStyle = {},
      phoneLoaderStyle = {},
      loaderCircleStyle = {},
      buttonStyle = {},
      buttonTextColor = {},
      buttonLogoFill = ''
    } = {}
  } = {}) => {
    const { isLoading } = this.state;

    return (
      <div style={actionStyle} className={`action ${buttonContainerClass}`}>
        {isLoading ? (
          <div className="phone-loader-container">
            <div style={phoneLoaderStyle} className={`phone-loader ${loaderClass}`}>
              <div style={loaderCircleStyle} className="circle" />
            </div>
          </div>
        ) : (
          <button
            className={`button ${buttonClass}`}
            type="submit"
            style={buttonStyle}
            onClick={(event) => this.handleActionButtonClick({ onOtpCodeValidate }, event)}
          >
            <span style={buttonTextColor} className="text">
              {this.getButtonText({ text })}
            </span>
            <span className={`icon ${iconClass}`}>
              <RoundedRight fill={buttonLogoFill} />
            </span>
          </button>
        )}
      </div>
    );
  };

  getThemeForIdentityModal = (key) => {
    const {
      homepage: { backgroundColor } = {},
      addToCartButton = {},
      themeTextColor
    } = getCatalogueTheme();

    switch (key) {
      case RENDER_PHONE_INPUT:
        return {
          titleStyle: { color: themeTextColor },
          styleForLeftContent: { borderColor: themeTextColor },
          styleForRightContent: { borderColor: themeTextColor },
          outerStyle: { borderColor: themeTextColor },
          outerInputStyle: { color: themeTextColor }
        };

      case RENDER_OTP_INPUT:
        return {
          titleStyle: { color: themeTextColor },
          styleForLeftContent: { borderColor: themeTextColor },
          styleForRightContent: { color: themeTextColor },
          outerStyle: { borderColor: themeTextColor },
          outerInputStyle: { color: themeTextColor }
        };

      case RENDER_ACTION_BUTTON:
        return {
          actionStyle: { backgroundColor },
          phoneLoaderStyle: { ...addToCartButton },
          loaderClass: {
            borderBottomColor: addToCartButton.textColor,
            borderRightColor: addToCartButton.textColor,
            borderLeftColor: addToCartButton.textColor
          },
          buttonStyle: { ...addToCartButton },
          buttonTextColor: { color: addToCartButton.textColor },
          buttonLogoFill: addToCartButton.textColor
        };

      default:
        break;
    }
  };

  render () {
    const { customRenderer } = this.props;
    const { intent, showResellButton, resellDownloadLink, otpFlowStarted, showResendOtp, error } =
      this.state;

    if (!intent) {
      return null;
    }

    let formContent = null;

    const {
      homepage: { backgroundColor } = {},
      addToCartButton = {},
      backIconColor = '#000000'
    } = getCatalogueTheme();

    if (typeof customRenderer === 'function') {
      formContent = customRenderer({
        showResendOtp,
        otpFlowStarted,
        onResendCode: this.onResendCode,
        onClose: this.onClose,
        renderPhoneInput: this.renderPhoneInput,
        renderOtpInput: this.renderOtpInput,
        renderActionButton: this.renderActionButton,
        containerClass: 'request-identity-modal'
      });
    } else {
      formContent = (
        <div className="request-identity-modal">
          <div className="container" style={{ backgroundColor }}>
            <div className="content">
              <div className="close">
                <div onClick={this.onClose}>
                  <Multiply size={15} color={backIconColor} />
                </div>
              </div>

              {intent === INTENT.ADD_INQUIRY && (
                <div className="text-container" style={{ color: backIconColor }}>
                  <div className="text-2">{I18n.t('REQUEST_IDENTITY_MODAL_ENTER_PHONE')}</div>
                </div>
              )}

              {this.renderPhoneInput({
                theme: this.getThemeForIdentityModal(RENDER_PHONE_INPUT)
              })}
              {this.renderOtpInput({
                theme: this.getThemeForIdentityModal(RENDER_OTP_INPUT)
              })}
              {error && <OTPError error={error} />}
            </div>

            {this.renderActionButton({
              theme: this.getThemeForIdentityModal(RENDER_ACTION_BUTTON)
            })}

            {showResellButton && (
              <div className="resell-button-container">
                <div className="resell-button-wrapper" onClick={this.onResellClick}>
                  <a
                    href={resellDownloadLink}
                    style={{ ...addToCartButton }}
                    className="resell-button"
                    target="_blank" rel="noreferrer"
                  >
                    <img className="logo" src={`${cdnHost}assets/logo.png`} alt="" />
                    <div style={{ color: addToCartButton.textColor }} className="resell-text">
                      Resell catalogue
                    </div>
                    <ArrowLongRight
                      width={24}
                      height={14}
                      className="arrow-right"
                      color={addToCartButton.textColor}
                    />
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <Modal isVisible={intent} onBackdropClick={this.onClose}>
        <form name="requestIdentifyForm" onSubmit={this.handleActionButtonClick} noValidate>
          {formContent}
        </form>
      </Modal>
    );
  }
}

export default RequestIdentityModal;
